const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001';

export type PosProviderType = 'rhino' | 'ambit' | 'shopify' | 'tucan' | 'softrestaurant';

/** What the merchant sees for each provider: the connect choices, the connected card, the disconnect dialog. */
export const POS_PROVIDER_DISPLAY_NAMES: Record<PosProviderType, string> = {
  rhino: 'Rhino POS',
  ambit: 'Ambit (pidedirecto)',
  shopify: 'Shopify',
  tucan: 'Tucán',
  softrestaurant: 'SoftRestaurant (Movete)',
};

/** Mirrors menteo-agent POS_PROVIDER_CAPABILITIES' FETCH_LOCATIONS (pos-provider-capabilities.ts). */
export const LOCATION_SYNCING_POS_TYPES: ReadonlySet<PosProviderType> = new Set(['rhino', 'shopify', 'tucan']);
/** Providers that use a branch's clientExternalId without syncing locations: SoftRestaurant push, Ambit menu sync. */
export const EXTERNAL_ID_DEPENDENT_POS_TYPES: ReadonlySet<PosProviderType> = new Set(['softrestaurant', 'ambit']);

/** Where the provider's current run is. `idle` also means "the last run finished". */
export type PosSyncStatus = 'idle' | 'queued' | 'running';

/** What asked for a run. Only `auto` can rest a provider after repeated failures. */
export type PosSyncTrigger = 'manual' | 'auto' | 'connect';

/** Stage of a run: asking which branches exist, pulling their menus, writing them. */
export type PosSyncPhase = 'locations' | 'menus' | 'saving';

/**
 * The backend's own marker for a run nobody finished (the service restarted mid-sync). Every other
 * value in `lastError.message` is a raw POS message we show verbatim; this one we translate.
 */
export const POS_SYNC_INTERRUPTED_ERROR = 'SYNC_INTERRUPTED';

export interface PosSyncError {
  /** Raw POS failure, or POS_SYNC_INTERRUPTED_ERROR. */
  message: string;
  at: string;
  trigger: PosSyncTrigger;
}

/**
 * Everything needed to answer "is my menu up to date, and if not, why not".
 *
 * `lastSyncAt` is the last SUCCESS and `lastError` the last FAILURE, deliberately kept apart: the
 * state that matters most — a menu from two days ago whose refresh has been failing since — is
 * exactly the one a single "last sync" timestamp hides.
 */
export interface PosSyncState {
  status: PosSyncStatus;
  trigger: PosSyncTrigger | null;
  queuedAt: string | null;
  startedAt: string | null;
  phase: PosSyncPhase | null;
  progress: { completed: number; total: number } | null;
  lastSyncAt: string | null;
  lastDurationMs: number | null;
  lastLocationsCount: number | null;
  lastError: PosSyncError | null;
  /** When the scheduler will next pick this provider up. */
  nextSyncAt: string | null;
  /** Automatic syncs are resting until this time after repeated failures. */
  suspendedUntil: string | null;
  consecutiveFailures: number;
}

/**
 * What a provider's order push can carry, computed on the server (C9) so this UI never branches on
 * a provider name.
 */
export interface PosDiscountCapabilities {
  /** The push carries a merchant coupon or promotion discount. */
  orderDiscounts: boolean;
  /** The push carries a merchant discount on the delivery fee (`appliesToDeliveryFee` / `DELIVERY_FEE`). */
  deliveryFeeDiscounts: boolean;
}

export interface BrandPosProviderDto {
  id: string;
  brandId: string;
  type: PosProviderType;
  enabled: boolean;
  lastMenuSyncAt?: string | null;
  lastTimetableSyncAt?: string | null;
  sync: PosSyncState;
  /** Absent on an API that predates it — `capabilitiesForBrand` reads that as "no limit". */
  discountCapabilities?: PosDiscountCapabilities;
  /** Rhino only: how many POS accounts the connection logs into. Absent means one (or not applicable). */
  accountCount?: number;
  /**
   * False for a push-only provider (SoftRestaurant): it only receives orders, so there is no sync to
   * run, retry or report. Absent on an API that predates it, which reads as true (`!== false`).
   */
  syncsData?: boolean;
  createdAt?: string;
  updatedAt?: string;
}

const UNRESTRICTED_DISCOUNT_CAPABILITIES: PosDiscountCapabilities = {
  orderDiscounts: true,
  deliveryFeeDiscounts: true,
};

/**
 * The brand's discount capabilities, from its provider rows (C9): the ENABLED row's answer, and
 * everything allowed when no row is enabled — a brand with no POS has no push to survive.
 */
export function capabilitiesForBrand(rows: readonly BrandPosProviderDto[]): PosDiscountCapabilities {
  const enabled = rows.find((row) => row.enabled);
  return enabled?.discountCapabilities ?? UNRESTRICTED_DISCOUNT_CAPABILITIES;
}

/** True while a run is queued or working — the state the UI animates. */
export function isPosSyncActive(provider: BrandPosProviderDto | undefined | null): boolean {
  return provider?.sync.status === 'queued' || provider?.sync.status === 'running';
}

/**
 * Error carrying the backend's typed `code` (e.g. SHOPIFY_MASTER_NOT_CONNECTED) alongside the message.
 *
 * Note the code is *always* populated: the API's exception filter falls back to a generic per-status
 * code (`BAD_REQUEST`, `CONFLICT`, …) when the thrown exception declares none. So `code` being set
 * says nothing about the error being one the UI understands — match against known codes, never on
 * truthiness.
 */
export class ApiError extends Error {
  code?: string;
  status?: number;
  /** The body's `details` object, when the refusal carries structured numbers (C11). */
  details?: Record<string, unknown>;
  constructor(message: string, opts?: { code?: string; status?: number; details?: Record<string, unknown> }) {
    super(message);
    this.name = 'ApiError';
    this.code = opts?.code;
    this.status = opts?.status;
    this.details = opts?.details;
  }
}

/**
 * How the UI should present a failed POS call.
 *
 * `retryable` means nothing is wrong with what the merchant entered and the same action may well
 * succeed shortly. `failure` means they have to change something first. The two get different toast
 * severities — see PosProvidersTab.
 */
export type PosErrorKind = 'retryable' | 'failure';

export function posErrorKind(err: unknown): PosErrorKind {
  return isTemporarilyUnavailableError(err) ? 'retryable' : 'failure';
}

/**
 * True when the request failed for a reason that has nothing to do with what the merchant entered —
 * today, the backend could not reach the queue that runs syncs (503).
 *
 * A sync already being in flight is NOT one of these any more: the backend answers with that run's
 * state instead of a conflict, because the operator asking for a fresh menu and the run already
 * fetching one want the same thing.
 */
export function isTemporarilyUnavailableError(err: unknown): boolean {
  return err instanceof ApiError && err.status === 503;
}

/**
 * The Shopify activation gates: states the merchant has to resolve in munchies-api (the master-client
 * "coupon provider" connection) before this brand can be activated. Each maps to a
 * `pos.shopify.gate.<code>` string, except SHOPIFY_CURRENCY_MISMATCH whose backend message names the
 * two currencies and is shown verbatim.
 *
 * Kept as an explicit allowlist rather than a `SHOPIFY_*` prefix test or a truthiness check on
 * `ApiError.code`, because the filter codes *every* error — gating on anything looser would render
 * the gate panel for unrelated failures (a 409 sync-in-progress, a 404, a 500).
 */
export const SHOPIFY_GATE_CODES = [
  'SHOPIFY_MASTER_NOT_CONNECTED',
  'SHOPIFY_REAUTH_REQUIRED',
  'SHOPIFY_SCOPES_MISSING',
  'SHOPIFY_CURRENCY_MISMATCH',
] as const;

export type ShopifyGateCode = (typeof SHOPIFY_GATE_CODES)[number];

export function isShopifyGateError(err: unknown): err is ApiError & { code: ShopifyGateCode } {
  return err instanceof ApiError && SHOPIFY_GATE_CODES.includes(err.code as ShopifyGateCode);
}

export interface ValidatedProviderResult {
  /** `accountLabel` (Rhino only) names the account a branch came from: its label, else its email. */
  locations: { externalId: string; name: string; accountLabel?: string }[];
}

/** One Rhino login. A brand whose negocios live under separate accounts connects several. */
export type RhinoAccount = { email: string; password: string; label?: string };

/** The Rhino credentials payload — always this shape, even for a single account. */
export type RhinoCredentials = { accounts: RhinoAccount[] };

/** One SoftRestaurant store per branch: `storeId` equals that branch's "ID Externo (POS)". */
export type SoftRestaurantStore = { storeId: string; storepassword: string };

/** The SoftRestaurant credentials payload. The API URL is global (server env), never sent from here. */
export type SoftRestaurantCredentials = { stores: SoftRestaurantStore[] };

/** A manual "attach this modifier group to every item in this category" mapping. */
export interface CatalogModifierOverride {
  categoryName: string;
  modifierGroupName: string;
}

/** Raw POS menu structure (names only) used to configure overrides. */
export interface PosMenuStructure {
  categories: { name: string; itemCount: number }[];
  modifierGroups: { name: string; options: { name: string; priceDelta: number }[] }[];
}

/** An item whose base POS price differs from the Ambit web price. */
export interface PosPriceDiff {
  /** POS menu item id — matches the catalog item's `id`, so the UI can overlay it inline. */
  menuItemId: string;
  name: string;
  basePrice: number;
  webPrice: number;
}

async function api<T>(endpoint: string, options: RequestInit = {}): Promise<T> {
  const res = await fetch(`${API_BASE_URL}${endpoint}`, {
    ...options,
    credentials: 'include',
    headers: { 'Content-Type': 'application/json', ...(options.headers as Record<string, string>) },
  });

  if (!res.ok) {
    let message = `Request failed: ${res.status}`;
    let code: string | undefined;
    try {
      const body = await res.json();
      message = body?.message ?? message;
      // The wire field is `error_code` (ApiErrorResponseDto); `code` is only the name the backend
      // exceptions use internally and never reaches the response body.
      code = typeof body?.error_code === 'string' ? body.error_code : undefined;
    } catch {
      // ignore
    }
    throw new ApiError(message, { code, status: res.status });
  }

  if (res.status === 204) return undefined as T;
  return res.json() as Promise<T>;
}

export const posProvidersClient = {
  getProviders: (brandId: string) => api<BrandPosProviderDto[]>(`/pos-providers/${brandId}`),

  validate: (type: PosProviderType, credentials: Record<string, unknown>) =>
    api<ValidatedProviderResult>('/pos-providers/validate', {
      method: 'POST',
      body: JSON.stringify({ type, credentials }),
    }),

  /**
   * Validates the credentials and registers the connection. Answers 202: the first pull runs in the
   * background, so the returned row describes a QUEUED sync, not an imported menu. Follow it with
   * `pollPosProviderSync` — except for a provider with `syncsData: false`, which queues nothing and is
   * fully connected once this returns.
   */
  connect: (brandId: string, type: PosProviderType, credentials: Record<string, unknown>) =>
    api<BrandPosProviderDto>(`/pos-providers/${brandId}/connect`, {
      method: 'POST',
      body: JSON.stringify({ type, credentials }),
    }),

  disconnect: (id: string) => api<void>(`/pos-providers/${id}`, { method: 'DELETE' }),

  /** Queues a re-sync and answers with the run's state. Idempotent while a run is already going. */
  sync: (brandId: string, type: PosProviderType) =>
    api<BrandPosProviderDto>(`/pos-providers/${brandId}/sync/${type}`, { method: 'POST' }),

  /** Raw POS menu structure (category + modifier-group names) for configuring overrides. */
  getMenuStructure: (brandId: string) => api<PosMenuStructure>(`/pos-providers/${brandId}/menu-structure`),

  /** Items whose base POS price differs from the Ambit web price, for one location. */
  getPriceComparison: (brandId: string, locationId: string) =>
    api<PosPriceDiff[]>(`/pos-providers/${brandId}/locations/${locationId}/price-comparison`),

  getModifierOverrides: (brandId: string) =>
    api<CatalogModifierOverride[]>(`/pos-providers/${brandId}/modifier-overrides`),

  setModifierOverrides: (brandId: string, overrides: CatalogModifierOverride[]) =>
    api<CatalogModifierOverride[]>(`/pos-providers/${brandId}/modifier-overrides`, {
      method: 'PUT',
      body: JSON.stringify({ overrides }),
    }),
};

/** How often a run in flight is checked. Syncs take minutes, so this is about feeling live, not precision. */
export const POS_SYNC_POLL_INTERVAL_MS = 3000;

/**
 * Follows one provider's run to the end.
 *
 * Polling rather than streaming on purpose: this is a page a merchant opens for a minute, and a
 * sync's whole observable state already lives on the row — there is nothing an SSE channel would add
 * except a connection to keep alive.
 *
 * A poll that fails is ignored, not fatal: the run keeps going on the server regardless of whether
 * this tab could reach it, and giving up on one flaky request would strand the UI mid-animation.
 */
export async function pollPosProviderSync(
  brandId: string,
  type: PosProviderType,
  options: {
    onUpdate?: (provider: BrandPosProviderDto) => void;
    signal?: AbortSignal;
    intervalMs?: number;
  } = {},
): Promise<BrandPosProviderDto | null> {
  const intervalMs = options.intervalMs ?? POS_SYNC_POLL_INTERVAL_MS;
  let latest: BrandPosProviderDto | null = null;

  for (;;) {
    if (options.signal?.aborted) return latest;

    try {
      const providers = await posProvidersClient.getProviders(brandId);
      const provider = providers.find((p) => p.type === type);
      if (provider) {
        latest = provider;
        options.onUpdate?.(provider);
        if (!isPosSyncActive(provider)) return provider;
      }
    } catch {
      // Transient: keep watching.
    }

    await new Promise((resolve) => setTimeout(resolve, intervalMs));
  }
}
